import type { AdminFeedback } from '@steado/admin-contract/feedback';
import { ExternalLinkIcon } from 'lucide-react';

import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { formatDateTime, formatRelative } from '@/lib/format';

import {
  FEEDBACK_KIND_LABELS,
  FEEDBACK_KIND_TONES,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_STATUS_TONES,
  appDetailsOf,
} from './feedback-labels';

interface FeedbackDrawerProps {
  readonly report: AdminFeedback;
  readonly canMutate: boolean;
  readonly onClose: () => void;
  /** Absent when the screen renders without a router, as its test does. */
  readonly onOpenUser?: ((userId: string) => void) | undefined;
  readonly onAnswer: (as: 'resolve' | 'dismiss') => void;
}

/**
 * One report, read in full.
 *
 * The table clamps a message to three lines because a queue is scanned, not
 * read; this is where it is read. The message keeps the sender's own line
 * breaks, since a bug report is often a numbered list of steps and flattening
 * it loses the order that makes it reproducible.
 */
export function FeedbackDrawer({ report, canMutate, onClose, onOpenUser, onAnswer }: FeedbackDrawerProps) {
  const details = appDetailsOf(report);
  const answered = report.status !== 'open';

  return (
    <Drawer
      title={report.kind === 'bug' ? 'Bug report' : 'Idea'}
      subtitle={`${report.displayName ?? 'Unnamed'} · ${formatDateTime(report.createdAt)}`}
      onClose={onClose}
      footer={
        answered || !canMutate ? undefined : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onAnswer('resolve');
              }}
            >
              Mark resolved
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onAnswer('dismiss');
              }}
              className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
            >
              Dismiss
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <BadgeCell label={FEEDBACK_KIND_LABELS[report.kind]} tone={FEEDBACK_KIND_TONES[report.kind]} />
          <BadgeCell
            label={FEEDBACK_STATUS_LABELS[report.status]}
            tone={FEEDBACK_STATUS_TONES[report.status]}
            dot
          />
          <span className="text-xs text-muted-foreground">{formatRelative(report.createdAt)}</span>
        </div>

        <Panel title="What they said">
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap text-foreground">
            {report.message}
          </p>
        </Panel>

        {answered ? (
          <Panel title="Outcome" bleed>
            <FieldRows>
              <FieldRow label="Status" value={FEEDBACK_STATUS_LABELS[report.status]} />
              <FieldRow label="Answered" value={formatDateTime(report.updatedAt)} />
              <FieldRow
                label="Note"
                value={
                  report.adminNote === null ? null : (
                    <span className="whitespace-pre-wrap">{report.adminNote}</span>
                  )
                }
              />
            </FieldRows>
          </Panel>
        ) : null}

        <Panel
          title="Sent by"
          bleed
          action={
            onOpenUser === undefined ? undefined : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onOpenUser(report.userId);
                }}
              >
                <ExternalLinkIcon aria-hidden="true" />
                Open user
              </Button>
            )
          }
        >
          <FieldRows>
            <FieldRow label="Name" value={report.displayName ?? 'Unnamed'} />
            <FieldRow label="Phone" value={report.phone} mono />
            <FieldRow label="Sent" value={formatDateTime(report.createdAt)} />
            <FieldRow
              label="App"
              value={details ?? 'Not included'}
              {...(details === null ? { tone: 'muted' as const } : { mono: true })}
            />
          </FieldRows>
        </Panel>
      </div>
    </Drawer>
  );
}
